import type { AdminAssessmentScore } from '~/api/types'
import { type StatTone } from '~/components/StatCard'
import { Card } from '~/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'
import { cn } from '~/lib/utils'
import { ratioTone } from './helpers'

// 달성 비율 → 진행 바 색(시맨틱 토큰).
const BAR_COLOR: Record<StatTone, string> = {
    default: 'bg-muted-foreground/40',
    primary: 'bg-primary',
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-destructive',
    info: 'bg-info',
}

// 항목별 점수 드릴다운(달성률 오름차순) — 취약 항목을 먼저 노출한다.
export function ScoreDrilldownTable({
    sortedScores,
}: {
    sortedScores: AdminAssessmentScore[]
}) {
    return (
        <Card className='p-5'>
            <h3 className='krds-title text-foreground mb-3'>항목별 점수</h3>
            {sortedScores.length === 0 ? (
                <p className='text-muted-foreground text-sm'>
                    점수 항목이 없어요.
                </p>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>항목</TableHead>
                            <TableHead>대분류</TableHead>
                            <TableHead className='whitespace-nowrap'>
                                점수
                            </TableHead>
                            <TableHead className='w-[34%]'>달성도</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {sortedScores.map((s) => {
                            const r =
                                s.maxScore != null && s.maxScore > 0
                                    ? (s.score ?? 0) / s.maxScore
                                    : null
                            const sp = r != null ? Math.round(r * 100) : null
                            const tone = ratioTone(r)
                            return (
                                <TableRow key={s.itemId}>
                                    <TableCell className='text-foreground font-medium'>
                                        {s.itemTitle || s.itemId}
                                    </TableCell>
                                    <TableCell>
                                        {s.majorCategory ?? '-'}
                                    </TableCell>
                                    <TableCell className='whitespace-nowrap tabular-nums'>
                                        {s.score ?? '-'}
                                        {s.maxScore != null
                                            ? ` / ${s.maxScore}`
                                            : ''}
                                    </TableCell>
                                    <TableCell>
                                        <ScoreBar pct={sp} tone={tone} />
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            )}
        </Card>
    )
}

// 달성도 진행 바 + 우측 퍼센트. 의미색으로 취약/충분을 한눈에.
function ScoreBar({ pct, tone }: { pct: number | null; tone: StatTone }) {
    if (pct == null)
        return <span className='text-muted-foreground text-[13px]'>-</span>
    return (
        <div className='flex items-center gap-2'>
            <div className='bg-secondary h-1.5 flex-1 overflow-hidden rounded-full'>
                <div
                    className={cn('h-full rounded-full', BAR_COLOR[tone])}
                    style={{ width: `${Math.min(100, pct)}%` }}
                />
            </div>
            <span className='text-muted-foreground w-9 shrink-0 text-right text-[12px] tabular-nums'>
                {pct}%
            </span>
        </div>
    )
}
